import { useCallback, useMemo } from 'react';
import { DataListItem, HeaderListItem } from '../types';
import { ROW_TYPES } from '../tableConfigs';
import { getCopyText, serializeId } from 'utils';
import { GenericTeamInterface, TeamMembershipType } from 'types';
import { Tenant } from 'TenantModule/types';
import { useAccountImpersonationMenu } from 'TeamModule/components/ImpersonateAccountMenu/useAccountImpersonationMenu';
import { useTeamMemberInfoModal } from 'TeamModule/components/MemberInfoModal/useTeamMemberInfoModal';
import { useSearchTeamsByFieldsQuery } from 'TeamModule/query/hooks/useSearchTeamsByFieldsQuery';
import useTeams from 'hooks/useTeams';
import useNavigationContext from 'hooks/useNavigationContext';
import useSandboxes from 'SandboxModule/hooks/useSandboxes';

/** The team facts the rows need: name and id for copy text, tags for impersonation. */
type TeamContext = Pick<GenericTeamInterface, 'id' | 'name'> & { tags: string[] };

export const useTeamMemberListItemsBuilder = ({ teamId }: { teamId: number }) => {
  const { openAccountImpersonationMenu } = useAccountImpersonationMenu();
  const { openTeamMemberInfoModal } = useTeamMemberInfoModal();

  const onRowEditClick = useCallback(
    ({ accountId, event }: { accountId: number; event: React.MouseEvent }) => {
      event.stopPropagation();
      openTeamMemberInfoModal({ accountId, teamId });
    },
    [openTeamMemberInfoModal, teamId],
  );
  // Use navigation context (URL params > global state) as source of truth for tenant/realm
  const navigationContext = useNavigationContext();
  const { getSandbox } = useSandboxes();
  const { currentTeam } = useTeams({
    shouldAutoLoad: false,
  });

  // Get realm object from sandbox cache for getCopyText display formatting
  const realm = navigationContext.realmId ? getSandbox(navigationContext.realmId) : undefined;

  // Same params as TeamProfilePage's call, so this reads its React Query cache
  // entry instead of making a new request.
  const { teamsSearch } = useSearchTeamsByFieldsQuery({
    tenant: navigationContext.tenant,
    realmId: navigationContext.realmId,
    teamId: String(teamId),
    enabled: !!teamId && !!navigationContext.tenant && !!navigationContext.realmId,
    limit: 1,
  });
  const searchRecord = teamsSearch?.data?.[0];

  // GLX-2075: rails teams/all (currentTeam) can take ~25 s on large teams, so
  // until it returns the team's name and tags come from the fast lookup record.
  // keepPreviousData can leave that record describing the previously viewed
  // team, and numeric team ids repeat across tenants/realms, so it is only
  // trusted when it identifies the exact team on screen (as isTeamDataCurrent
  // in TeamProfilePage). The lookup omits tags for an untagged team, hence
  // `?? []` on a matching record. With no known team the context is null and
  // impersonation waits: [] is never a placeholder, because the impersonate
  // endpoint refuses Sales/QA/External roles without their team tag.
  const teamContext = useMemo<TeamContext | null>(() => {
    if (currentTeam?.id) {
      return { id: currentTeam.id, name: currentTeam.name, tags: currentTeam.tags ?? [] };
    }
    const isSearchRecordCurrent =
      searchRecord?.teamId === String(teamId) &&
      searchRecord?.tenant === navigationContext.tenant &&
      searchRecord?.realmId === navigationContext.realmId;
    if (searchRecord && isSearchRecordCurrent) {
      return { id: teamId, name: searchRecord.name, tags: searchRecord.tags ?? [] };
    }
    return null;
  }, [currentTeam, searchRecord, teamId, navigationContext.tenant, navigationContext.realmId]);

  const listItemBuilder = useCallback(
    ({ teamMemberships }: { teamMemberships: TeamMembershipType[] }) => {
      const headerListItem = {
        id: ROW_TYPES.HEADER,
        rowType: ROW_TYPES.HEADER,
        disableRowHover: false,
        isStickyHeader: true,
      } as HeaderListItem;

      const formattedAccounts: DataListItem[] = teamMemberships.map((member) => {
        const onImpersonate: DataListItem['onImpersonate'] = (e) => {
          if (!teamContext) return;
          openAccountImpersonationMenu({
            target: e.currentTarget,
            accountId: member.account_id,
            teamId,
            tags: teamContext.tags,
            // Use navigation context for impersonation (priority: URL params > auth defaults)
            // If undefined, navigateToMosaicAppImpersonated falls back to ApiClientManager.getAuthDefaultRealmId()
            tenant: navigationContext.tenant,
            realmId: navigationContext.realmId,
          });
        };

        const textToCopy = getCopyText({
          tenant: navigationContext.tenant
            ? ({ tenantName: navigationContext.tenant } as Tenant)
            : undefined,
          realm,
          team: teamContext ? { id: teamContext.id, name: teamContext.name } : undefined,
          email: member.email,
          accountId: member.account_id,
        });

        return {
          id: serializeId([member.account_id]),
          rowType: ROW_TYPES.DATA,
          member,
          onImpersonate,
          isLoadingImpersonation: !teamContext,
          onRowClick: (event) => onRowEditClick({ accountId: member.account_id, event }),
          textToCopy,
        };
      });

      return [headerListItem, ...formattedAccounts];
    },
    [
      teamContext,
      realm,
      teamId,
      onRowEditClick,
      openAccountImpersonationMenu,
      navigationContext.tenant,
      navigationContext.realmId,
    ],
  );

  return listItemBuilder;
};
